import { z } from 'zod';
import { createZodBasicOperationSchema, zCreateOperationParentContext } from '~/server/logic/steps/operation/types';
import { zRecorderExecutionType } from '~/common/vm/shared-types';
import type { FramedHandle } from '~/server/logic/playwright/locator/get-element-handle';

export const zPosition = z.object({
  x: z.number(),
  y: z.number(),
});
export type Position = z.output<typeof zPosition>;

export const zSwipeDirection = z.enum(['up', 'down', 'left', 'right']);
export type SwipeDirection = z.output<typeof zSwipeDirection>;
export const isSwipeDirection = (value: string): value is SwipeDirection => zSwipeDirection.safeParse(value).success;

export const zSwipe = z.object({
  x: z.number(),
  y: z.number(),
  direction: z.enum(['up', 'down', 'left', 'right']),
});

export type Swipe = z.output<typeof zSwipe>;

export const zScrollDirection = z.enum(['up', 'down', 'left', 'right']);
export type ScrollDirection = z.output<typeof zScrollDirection>;
export const isScrollDirection = (value: string): value is ScrollDirection => zScrollDirection.safeParse(value).success;

export const zScrollOperationValue = z.object({
  direction: zScrollDirection,
  amount: z.number(),
});

export const zBrowserWheelEventScroll = z.object({
  pageAlias: z.string().optional().default('page'),
  type: z.literal('scroll'),
  direction: zScrollDirection,
  position: zPosition,
  amount: z.number(),
});

export const zRecorderOperation = createZodBasicOperationSchema(zPosition.optional());
export type RecorderOperation = z.infer<typeof zRecorderOperation>;

export const zOperationWithHandle = createZodBasicOperationSchema(z.custom<FramedHandle>().optional());
export type OperationWithHandlePair = z.infer<typeof zOperationWithHandle>;

export const zClickMode = z.enum(['center', 'relative']);
export type ClickMode = z.output<typeof zClickMode>;

const zOperation = z.object({
  pageAlias: z.string().optional().default('page'),
  type: z.literal('operation'),
  operation: zRecorderOperation,
  // We are being a bit lazy and adding clickMode here since it's an ephermal option that's not saved into the operation
  // By keeping it at this level, we avoid polluting the operation object with ephermal options
  // Note: I personally think we should just store this with the operation, but I'm mainting status quo right now.
  clickMode: zClickMode.optional().default('center'),
});

export const zPrompt = z.object({
  type: z.literal('prompt'),
  promptValue: z.string(),
});

export const zFileUpload = z.object({
  type: z.literal('file-upload'),
  fileIds: z.array(z.string()),
});

const zTabSwitch = z.object({
  type: z.literal('tab-switch'),
  pageAlias: z.string(),
});

const zTerminate = z.object({
  type: z.literal('terminate'),
});

// This isn't a mouse cursor, the recorder recording position itself (insertion point)
const zSetRecorderCursor = z.object({
  type: z.literal('set-cursor'),
  // stepId is always required for:
  // - UI invalidation and state management
  // - Context about where the component operation exists within the step
  // - Recorder cursor positioning
  stepId: z.string(),
  // parentContext is optional and indicates that we're in a parent op (component or conditional)
  parentContext: zCreateOperationParentContext.optional(),
  stepOrder: z.number(),
  operation: z
    .object({
      id: z.string(),
      order: z.number(),
    })
    .optional(),
});

export const zRecorderCommand = z.discriminatedUnion('type', [
  zFileUpload,
  zOperation,
  zPrompt,
  zBrowserWheelEventScroll,
  zTabSwitch,
  zTerminate,
  zSetRecorderCursor,
  z.object({
    type: z.literal('toggle-pause'),
  }),
  z.object({
    type: z.literal('cancel-replay'),
  }),
  z.object({
    type: z.literal('stop-replay-and-record'),
    cursorStepId: z.string(),
    cursorOperationId: z.string().optional(),
  }),
  z.object({
    pageAlias: z.string().optional().default('page'),
    type: z.literal('ai-step'),
    prompt: z.string(),
  }),
  z.object({
    type: z.literal('switch-mode'),
    mode: zRecorderExecutionType,
    cursorStepId: z.string(),
    cursorStepOrder: z.number(),
    cursorOperation: z.object({ id: z.string(), order: z.number() }).optional(),
    endStepAndOp: z.object({ stepId: z.string(), operationOrder: z.number() }).optional(),
  }),
  z.object({
    pageAlias: z.string().optional().default('page'),
    type: z.literal('playwright-action'),
    value: z.string().min(1),
  }),
]);
export type RecorderCommand = z.input<typeof zRecorderCommand>;
